import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatHint } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HospitalDetailsComponent } from '../hospital-details/hospital-details.component';
import { HospitalService } from '../hospital-details/hospital.service';
import { ScrolldDirective } from '../scrolld.directive';
import { AppointmentUiComponent } from '../appointment-ui/appointment-ui.component';
import { HealthcareServiceEditorDialogComponent } from '../healthcare-services-v2/healthcare-service-editor-dialog.component';
import { HealthcareService } from 'fhir/r4';
import { AdminDashboardService, DashboardMetrics } from './admin-dashboard.service';
import { BehaviorSubject, Observable, combineLatest, finalize, map, startWith, switchMap, shareReplay, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type AppointmentStatusTab = 'active' | 'completed' | 'all';


@Component({
  selector: 'app-admin-dashboard',
  imports: [MatIconModule, MatTooltipModule, MatCardModule, AppointmentUiComponent,
    ReactiveFormsModule, MatSelectModule, ScrolldDirective, MatDialogModule, MatProgressSpinnerModule, AsyncPipe
  ],
  templateUrl: './admin-dashboard.component.html',
  styleUrl: './admin-dashboard.component.scss'
})
export class AdminDashboardComponent implements OnInit {
  encounterDropDown = new FormControl("All")
  patientsDropDown = new FormControl("All");
  todayAppointSelect = new FormControl("Booked");
  timeRange = new FormControl("Today");

  private dashboardService = inject(AdminDashboardService);
  hospital = inject(HospitalService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly metricsLoadingSubject = new BehaviorSubject<boolean>(true);
  metricsLoading$ = this.metricsLoadingSubject.asObservable();

  metrics$: Observable<DashboardMetrics> = this.timeRange.valueChanges.pipe(
    startWith(this.timeRange.value),
    tap(() => this.metricsLoadingSubject.next(true)),
    switchMap(range => this.dashboardService.getMetrics(range ?? 'Today').pipe(
      finalize(() => this.metricsLoadingSubject.next(false))
    )),
    shareReplay(1)
  );

  appointmentDisplayValue$!: Observable<number>;
  encounterDisplayValue$!: Observable<number>;
  patientDisplayValue$!: Observable<number>;

  // New Summary Observables
  newRequests$!: Observable<number>;
  pendingAppts$!: Observable<number>;
  confirmedAppts$!: Observable<number>;
  waitlistAppts$!: Observable<number>;
  bedsAvailable$!: Observable<number>;
  bedsTotal$!: Observable<number>;
  outOfStockMedicines$!: Observable<number>;
  totalMedicines$!: Observable<number>;

  ngOnInit() {
    this.metrics$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.appointmentDisplayValue$ = combineLatest([
      this.metrics$,
      this.todayAppointSelect.valueChanges.pipe(startWith(this.todayAppointSelect.value))
    ]).pipe(
      map(([metrics, selection]) => {
        switch (selection) {
          case 'Fulfilled': return metrics.appointments.fulfilled;
          case 'No-Show': return metrics.appointments.noShow;
          case 'Remaining': return metrics.appointments.remaining;
          case 'Booked': return metrics.appointments.booked;
          default: return metrics.appointments.total;
        }
      })
    );

    this.encounterDisplayValue$ = combineLatest([
      this.metrics$,
      this.encounterDropDown.valueChanges.pipe(startWith(this.encounterDropDown.value))
    ]).pipe(
      map(([metrics, selection]) => {
        switch (selection) {
          case 'In-Patient': return metrics.encounters.inpatient;
          case 'Out-Patient': return metrics.encounters.outpatient;
          case 'Emergency': return metrics.encounters.emergency;
          case 'Virtual': return metrics.encounters.virtual;
          case 'Home': return metrics.encounters.home;
          default: return metrics.encounters.total;
        }
      })
    );

    this.patientDisplayValue$ = this.metrics$.pipe(map(m => m.patients.total));

    // Initialize new summary observables
    this.newRequests$ = this.metrics$.pipe(map(m => m.appointments.currentProposed));
    this.pendingAppts$ = this.metrics$.pipe(map(m => m.appointments.currentPending));
    this.confirmedAppts$ = this.metrics$.pipe(map(m => m.appointments.currentBooked));
    this.waitlistAppts$ = this.metrics$.pipe(map(m => m.appointments.currentWaitlist));
    this.bedsAvailable$ = this.metrics$.pipe(map(m => m.resources.bedsAvailable));
    this.bedsTotal$ = this.metrics$.pipe(map(m => m.resources.bedsTotal));
    this.outOfStockMedicines$ = this.metrics$.pipe(map(m => m.resources.outOfStockMedicines));
    this.totalMedicines$ = this.metrics$.pipe(map(m => m.resources.totalMedicines));
  }

  openHealthcareServiceDialog(resource?: HealthcareService | null): void {
    this.dialog.open(HealthcareServiceEditorDialogComponent, {
      width: '920px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { resource: resource ?? null }
    });
  }

  addAvailableServices(): void {
    this.router.navigate(['/app/add-service-details']);
  }

  manageAppointments(status?: string | string[], tab?: AppointmentStatusTab, includeDateRange = true): void {
    const queryParams: Record<string, string | string[]> = {};
    if (includeDateRange && this.timeRange.value) {
      queryParams['dateRange'] = this.timeRange.value;
    }
    if (status) {
      queryParams['status'] = status;
    }
    if (tab) {
      queryParams['tab'] = tab;
    }

    this.router.navigate(['/app/appointments'], {
      queryParams
    });
  }

  manageSelectedAppointments(): void {
    const selection = this.todayAppointSelect.value;
    switch (selection) {
      case 'Fulfilled':
        this.manageAppointments('fulfilled', 'completed');
        return;
      case 'No-Show':
        this.manageAppointments('noshow', 'completed');
        return;
      case 'Remaining':
      case 'Booked':
        this.manageAppointments('booked', 'active');
        return;
      default:
        this.manageAppointments(undefined, 'all');
    }
  }

  manageCurrentAppointmentStatus(status: string, tab: AppointmentStatusTab = 'active'): void {
    this.manageAppointments(status, tab, false);
  }
}

